import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Grid, MenuItem, TextField } from "@mui/material";
import { Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

const selectFieldProps = {
  InputLabelProps: { shrink: true },
  sx: {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      alignItems: "center",
    },
  },
};

function AddAgriBoost() {
  const [redirect, setRedirect] = useState(false);
  const [allSubjects, setAllSubjects] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [notification, setNotification] = useState({ open: false, color: "success", message: "" });
  const [form, setForm] = useState({
    subject_id: "",
    topic_id: "",
    question_english: "",
    question_hindi: "",
    answer_english: "",
    answer_hindi: "",
    description_english: "",
    description_hindi: "",
    reveal_time: 45,
    display_order: 0,
    status: "draft",
    is_active: true,
  });

  const showMessage = (message, color = "success", shouldRedirect = false) => {
    setNotification({ open: true, color, message });
    setTimeout(() => {
      setNotification({ open: false, color, message: "" });
      if (shouldRedirect) setRedirect(true);
    }, 2500);
  };

  useEffect(() => {
    const loadSubjects = async () => {
      try {
        const resp = await axios.get(Constants.getUrls.getAgriBoostMetaSubjects);
        setAllSubjects(resp?.data?.data || []);
      } catch (error) {
        showMessage("Unable to load subjects.", "error");
      }
    };

    loadSubjects();
  }, []);

  useEffect(() => {
    const loadTopics = async () => {
      if (!form.subject_id) {
        setAllTopics([]);
        return;
      }

      try {
        const resp = await axios.get(
          `${Constants.getUrls.getAgriBoostMetaTopics}?subject_id=${form.subject_id}`
        );
        setAllTopics(resp?.data?.data || []);
      } catch (error) {
        showMessage("Unable to load topics.", "error");
      }
    };

    loadTopics();
  }, [form.subject_id]);

  const handleChange = (name, value) => {
    if (name === "subject_id") {
      setForm((prev) => ({ ...prev, subject_id: value, topic_id: "" }));
      return;
    }

    if (name === "is_active") {
      setForm((prev) => ({ ...prev, is_active: value === "true" }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(Constants.postUrls.createAgriBoost, form);
      showMessage("AgriBoost created successfully!", "success", true);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to create AgriBoost.", "error");
    }
  };

  if (redirect) return <Navigate to="/agriboost" />;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Create AgriBoost
            </MDTypography>
          </MDBox>
          <MDBox py={3} px={2}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Subject"
                    value={form.subject_id}
                    onChange={(e) => handleChange("subject_id", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="">Select Subject</MenuItem>
                    {allSubjects.map((item) => (
                      <MenuItem key={item._id} value={item._id}>
                        {item.subject_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Topic"
                    value={form.topic_id}
                    onChange={(e) => handleChange("topic_id", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="">Select Topic</MenuItem>
                    {allTopics.map((item) => (
                      <MenuItem key={item._id} value={item._id}>
                        {item.topic_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Reveal Time"
                    value={form.reveal_time}
                    onChange={(e) => handleChange("reveal_time", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Display Order"
                    value={form.display_order}
                    onChange={(e) => handleChange("display_order", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Question English"
                    value={form.question_english}
                    onChange={(e) => handleChange("question_english", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Question Hindi"
                    value={form.question_hindi}
                    onChange={(e) => handleChange("question_hindi", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Answer English"
                    value={form.answer_english}
                    onChange={(e) => handleChange("answer_english", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Answer Hindi"
                    value={form.answer_hindi}
                    onChange={(e) => handleChange("answer_hindi", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    multiline
                    minRows={4}
                    fullWidth
                    label="Description English"
                    value={form.description_english}
                    onChange={(e) => handleChange("description_english", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    multiline
                    minRows={4}
                    fullWidth
                    label="Description Hindi"
                    value={form.description_hindi}
                    onChange={(e) => handleChange("description_hindi", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={form.status}
                    onChange={(e) => handleChange("status", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Active Status"
                    value={String(form.is_active)}
                    onChange={(e) => handleChange("is_active", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
              <Grid align="left" mt={2}>
                <MDButton variant="contained" size="small" color="info" type="submit">
                  Create AgriBoost
                </MDButton>
              </Grid>
            </form>
          </MDBox>
          <MDSnackbar
            color={notification.color}
            icon="notifications"
            title="AgriQuiz"
            content={notification.message}
            dateTime="Just now"
            open={notification.open}
          />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default AddAgriBoost;
